// stores/useAuth.ts
import { create, StoreApi, UseBoundStore } from 'zustand';
import { toast } from 'react-toastify'; // Import toastify
import getErrorMessage from '../utils/error';
import { IAuthStore, ICredentials } from '../types/types';
import {
  changeEmail,
  getStatusAuth,
  getUserAuth,
  login,
  logout,
  sendEmailVerify,
  updateCompany,
  updateNameUser,
  updatePassword,
  verifyOtp,
} from '../utils/api';

interface IChangeEmail {
  newEmail: string;
}

const useAuth: UseBoundStore<StoreApi<IAuthStore>> = create((set) => ({
  dataUser: null,
  company: null,
  loading: false,
  isAuthenticated: false,
  login: async (credentials: ICredentials) => {
    const createToast = toast.loading('Login process...');
    try {
      set(() => ({ loading: true }));
      const { success, message } = await login(credentials);
      if (!success) {
        throw new Error(message);
      }
      toast.update(createToast, {
        position: 'top-right',
        render: 'Login successfully',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      return { success, message };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.update(createToast, {
        position: 'top-right',
        render: message,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      return { message, success: false };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  logout: async () => {
    try {
      set(() => ({ loading: true }));
      const { success, message } = await logout();
      if (!success) {
        throw new Error(message);
      }
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, {
        position: 'top-right',
      });
      return { message, success: false };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  checkAuth: async () => {
    try {
      set(() => ({ loading: true }));
      const { success, message } = await getStatusAuth();
      if (!success) {
        throw new Error(message);
      }
      set(() => ({ isAuthenticated: success }));
      return await Promise.resolve();
    } catch (error) {
      const message = getErrorMessage(error);
      set(() => ({ isAuthenticated: false }));
      return await Promise.reject(message);
    } finally {
      set(() => ({ loading: false }));
    }
  },
  userAuth: async () => {
    try {
      set(() => ({ loading: true }));
      const response = await getUserAuth();
      if (!response.success) {
        throw new Error(response.message);
      }
      set(() => ({ dataUser: response.data }));
      set(() => ({ company: response.data.company }));
      return { success: true, data: response.data };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, {
        position: 'top-right',
      });
      return { message, success: false };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  updateCompany: async (id, updateData) => {
    const createToast = toast.loading('Update company...');
    try {
      set(() => ({ loading: true }));
      const { success, message } = await updateCompany(id, updateData);
      if (!success) {
        throw new Error(message);
      }
      toast.update(createToast, {
        position: 'top-right',
        render: 'Update company successfully',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      set((state) => ({
        company: { ...state.company, ...updateData },
      }));
      return { success, message };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.update(createToast, {
        position: 'top-right',
        render: message,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      return { message, success: false };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  updateNameUser: async (id, updateData) => {
    const createToast = toast.loading('Update name...');
    try {
      set(() => ({ loading: true }));
      const { success, message } = await updateNameUser(id, updateData);
      if (!success) {
        throw new Error(message);
      }
      toast.update(createToast, {
        position: 'top-right',
        render: 'Update name successfully',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      set((state) => ({
        dataUser: { ...state.dataUser, name: updateData.name } as IAuthStore['dataUser'],
      }));
      return { success, message };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.update(createToast, {
        position: 'top-right',
        render: message,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      return { message, success: false };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  sendEmailVerify: async (id, data) => {
    const createToast = toast.loading('Sending email verification...');
    try {
      set(() => ({ loading: true }));
      const { success, message } = await sendEmailVerify(id, data);
      if (!success) {
        throw new Error(message);
      }
      toast.update(createToast, {
        position: 'top-right',
        render: 'OTP sent to your current email address',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      return { success, message };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.update(createToast, {
        position: 'top-right',
        render: message,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      return { message, success: false };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  verifyOtp: async (id, data) => {
    const createToast = toast.loading('Verification code...');
    try {
      set(() => ({ loading: true }));
      const { success, message } = await verifyOtp(id, data);
      if (!success) {
        throw new Error(message);
      }
      toast.update(createToast, {
        position: 'top-right',
        render: 'OTP verified successfully',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      return { success, message };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.update(createToast, {
        position: 'top-right',
        render: message,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      return { message, success: false };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  // changeEmail: async (id, data) => {
  //   const createToast = toast.loading('Changing email...');
  //   try {
  //     set(() => ({ loading: true }));
  //     const { success, message } = await changeEmail(id);
  //     if (!success) {
  //       throw new Error(message);
  //     }
  //     toast.update(createToast, {
  //       position: 'top-right',
  //       render: 'Email has been changed successfully.',
  //       type: 'success',
  //       isLoading: false,
  //       autoClose: 3000,
  //     });
  //     set((state) => ({
  //       dataUser: { ...state.dataUser, email: data.email } as IAuthStore['dataUser'],
  //     }));
  //     return { success, message };
  //   } catch (error) {
  //     const message = getErrorMessage(error);
  //     toast.update(createToast, {
  //       position: 'top-right',
  //       render: message,
  //       type: 'error',
  //       isLoading: false,
  //       autoClose: 3000,
  //     });
  //     return { message, success: false };
  //   } finally {
  //     set(() => ({ loading: false }));
  //   }
  // },
  changeEmail: async (id, data) => {
    const createToast = toast.loading('Changing email...');
    try {
      set(() => ({ loading: true }));
      const { success, message } = await changeEmail(id, data as IChangeEmail);
      if (!success) {
        throw new Error(message);
      }
      toast.update(createToast, {
        position: 'top-right',
        render: 'Email has been changed successfully.',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      set((state) => ({
        dataUser: { ...state.dataUser, email: data.newEmail } as IAuthStore['dataUser'],
      }));
      return { success, message };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.update(createToast, {
        position: 'top-right',
        render: message,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      return { message, success: false };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  changePass: async (id, data) => {
    const createToast = toast.loading('Changing password...');
    try {
      set(() => ({ loading: true }));
      const { success, message } = await updatePassword(id, data);
      if (!success) {
        throw new Error(message);
      }
      toast.update(createToast, {
        position: 'top-right',
        render: 'Password has been changed successfully.',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      return { success, message };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.update(createToast, {
        position: 'top-right',
        render: message,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      return { message, success: false };
    } finally {
      set(() => ({ loading: false }));
    }
  },
}));

// Subscribe untuk mengecek perubahan state
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// const unsubscribe = useAuth.subscribe((state) => {
//   console.log('State Auth:', state);
// });

export default useAuth;
